import { useEffect, useState } from 'react';
import { IonBadge, IonButton, IonIcon } from '@ionic/react';
import { refreshOutline, exitOutline } from 'ionicons/icons';
import { Card, Page } from '../components/Shell';
import { RideMap } from '../components/RideMap';
import { MapPicker } from '../components/MapPicker';
import { PickupEditor } from '../components/PickupEditor';
import { api } from '../lib/api';
import { clearBadge, setBadge } from '../lib/badge';
import { enablePush, pushSupport } from '../lib/push';
import { STATUS_COLOR, STATUS_LABEL, clockTime, isPositionFresh, timelineLabel } from '../lib/status';
import { formatAge, formatDistance, formatEtaShort } from '@shared/format.js';
import type { Coords, Ride } from '../lib/types';
import type { Session } from '../lib/session';

interface Props {
  session: Session;
  ride: Ride;
  onRide: (ride: Ride) => void;
  onLeave: () => void;
  onError: (message: string) => void;
}

/** Alle 15 Sekunden - häufiger als die geforderte Minute, aber noch sparsam. */
const POLL_INTERVAL_MS = 15_000;

export function PassengerWait({ session, ride, onRide, onLeave, onError }: Props) {
  const [refreshing, setRefreshing] = useState(false);
  // Solange bearbeitet wird, zeigt die Kartenfläche oben den editierbaren
  // Abholpunkt statt der Fahrtübersicht - eine Karte, kein zweiter Screen.
  const [editingPickup, setEditingPickup] = useState(false);
  const [pickupDraft, setPickupDraft] = useState<Coords | null>(ride.pickup);

  // Wer über einen Kontakt direkt hierher kommt, hat den Bestätigen-Bildschirm
  // (und damit den "Benachrichtigungen einschalten"-Knopf) nie gesehen. Ist
  // die Erlaubnis schon erteilt - etwa vom Verbinden mit dem Kontakt -, klappt
  // das hier ganz ohne Rückfrage; sonst bleibt es einfach bei reinem Nachschauen.
  useEffect(() => {
    if (ride.hasPassengerSubscription || pushSupport() !== 'granted') return;
    void enablePush(session.rideId, session.token).then((result) => {
      if (result.state === 'granted') void api.get(session.rideId, session.token).then(({ ride: fresh }) => onRide(fresh));
    });
    // Nur beim Betreten pruefen, nicht bei jeder Ride-Aktualisierung.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Solange nicht bearbeitet wird, dem geltenden Punkt folgen.
  useEffect(() => {
    if (!editingPickup) setPickupDraft(ride.pickup);
  }, [ride.pickup, editingPickup]);

  function startEditingPickup(draft: Coords | null) {
    setPickupDraft(draft ?? ride.pickup);
    setEditingPickup(true);
  }

  function cancelEditingPickup() {
    setPickupDraft(ride.pickup);
    setEditingPickup(false);
  }

  async function confirmPickup() {
    if (!pickupDraft) return;
    try {
      const { ride: fresh } = await api.setPickup(session.rideId, session.token, pickupDraft);
      onRide(fresh);
      setEditingPickup(false);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Abholpunkt konnte nicht gesetzt werden.');
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function refresh() {
      try {
        const { ride: fresh } = await api.get(session.rideId, session.token);
        if (!cancelled) onRide(fresh);
      } catch {
        // Verbindungsaussetzer: der nächste Durchlauf holt es nach.
      }
    }

    const timer = window.setInterval(refresh, POLL_INTERVAL_MS);
    // Beim Zurückkehren aus dem Hintergrund sofort aktualisieren.
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [session, onRide]);

  async function manualRefresh() {
    setRefreshing(true);
    try {
      const { ride: fresh } = await api.get(session.rideId, session.token);
      onRide(fresh);
    } catch {
      /* still */
    } finally {
      setRefreshing(false);
    }
  }

  // Restminuten als Zahl auf dem Home-Bildschirm-Icon, solange die Fahrt
  // läuft - der PWA-Ersatz für eine Live Activity (siehe lib/badge.ts).
  useEffect(() => {
    if (ride.status === 'enroute' || ride.status === 'arriving_soon') {
      if (ride.estimate) void setBadge(Math.max(1, Math.round(ride.estimate.etaSeconds / 60)));
    } else {
      void clearBadge();
    }
    return () => void clearBadge();
  }, [ride.status, ride.estimate?.etaSeconds]);

  const estimate = ride.estimate;
  const fresh = isPositionFresh(ride);
  const waiting = ride.status === 'created' || ride.status === 'waiting';

  return (
    <Page title={`${ride.driverName} holt dich ab`}>
      <Card>
        <div className="r2f-status">
          {waiting ? (
            <>
              <div className="r2f-status__eta" style={{ fontSize: '1.9rem' }}>
                Noch nicht losgefahren
              </div>
              <div className="r2f-status__label">
                Du bekommst Bescheid, sobald sich {ride.driverName} mehr als{' '}
                {formatDistance(ride.settings.departureRadiusM)} vom Startpunkt entfernt.
              </div>
            </>
          ) : ride.status === 'arrived' ? (
            <>
              <div className="r2f-status__eta">Da!</div>
              <div className="r2f-status__label">{ride.driverName} wartet am Abholpunkt.</div>
            </>
          ) : ride.status === 'cancelled' ? (
            <>
              <div className="r2f-status__eta" style={{ fontSize: '1.9rem' }}>Fahrt beendet</div>
              <div className="r2f-status__label">Diese Fahrt wurde abgebrochen.</div>
            </>
          ) : (
            <>
              <div className="r2f-status__eta">
                {estimate ? formatEtaShort(estimate.etaSeconds) : '-'}
              </div>
              <div className="r2f-status__label">
                {estimate
                  ? `noch ${formatDistance(estimate.routeMeters)} Fahrtweg`
                  : 'Position wird ermittelt'}
              </div>
            </>
          )}

          <div className="r2f-row" style={{ justifyContent: 'center', marginTop: 16, gap: 8 }}>
            <IonBadge color={STATUS_COLOR[ride.status]}>{STATUS_LABEL[ride.status]}</IonBadge>
            {ride.driver && (
              <span className={fresh ? 'r2f-pill r2f-pill--live' : 'r2f-pill r2f-pill--stale'}>
                <span className={fresh ? 'r2f-dot r2f-dot--pulsing' : 'r2f-dot'} />
                {fresh ? 'Live' : formatAge(ride.driver.ageSeconds)}
              </span>
            )}
          </div>
        </div>
      </Card>

      <Card flush>
        {editingPickup ? (
          <MapPicker
            value={pickupDraft}
            onChange={setPickupDraft}
            kind="pickup"
            tall
            secondary={ride.origin}
            secondaryKind="origin"
          />
        ) : (
          <RideMap ride={ride} />
        )}
      </Card>

      {ride.status !== 'arrived' && ride.status !== 'cancelled' && (
        <PickupEditor
          current={ride.pickup}
          editing={editingPickup}
          draft={pickupDraft}
          onStartEditing={startEditingPickup}
          onDraftChange={setPickupDraft}
          onConfirm={confirmPickup}
          onCancel={cancelEditingPickup}
          onError={onError}
        />
      )}

      {!fresh && ride.driver && (
        <div className="r2f-hint r2f-hint--warn">
          Die letzte Position ist {formatAge(ride.driver.ageSeconds)} eingegangen. Vielleicht ist
          gerade kein Empfang - die Schätzung kann daneben liegen.
        </div>
      )}

      {ride.timeline.length > 1 && (
        <Card>
          <h2>Verlauf</h2>
          <ul className="r2f-timeline">
            {[...ride.timeline].reverse().map((entry, index) => (
              <li key={`${entry.type}-${entry.at}-${index}`}>
                <time>{clockTime(entry.at)}</time>
                <span>
                  <strong>{timelineLabel(entry.type)}</strong>
                  {entry.text && <em>{entry.text}</em>}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="r2f-row" style={{ gap: 8 }}>
        <IonButton fill="outline" style={{ flex: 1 }} onClick={manualRefresh} disabled={refreshing}>
          <IonIcon slot="start" icon={refreshOutline} />
          Aktualisieren
        </IonButton>
        <IonButton fill="clear" color="medium" onClick={onLeave}>
          <IonIcon slot="start" icon={exitOutline} />
          Verlassen
        </IonButton>
      </div>

      <p className="r2f-muted">
        Die Karte aktualisiert sich nur, solange die App offen ist. Die drei wichtigen Meldungen
        erreichen dich als Benachrichtigung auch bei geschlossener App.
      </p>
    </Page>
  );
}
